import PageSEO from '../components/PageSEO';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Brain, Loader, CheckCircle, Share2, Crown, Sparkles, Clock } from 'lucide-react';
import ShareReportModal from '../components/ShareReportModal';
import GuideModal from '../components/GuideModal';
import DailyVitalsCard from '../components/DailyVitalsCard';
import TaskPlayerModal from '../components/TaskPlayerModal';
import { PremiumLock, PremiumCTA, PremiumReveal } from '../components/PremiumGate';
import { usePremium } from '../hooks/usePremium';

// Dashboard sub-components
import { SparkleStyles } from '../components/dashboard/DashboardStyles';
import { BackgroundLayers } from '../components/dashboard/DashboardShared';
import TrendChartPanel from '../components/dashboard/TrendChartPanel';
import AchievementsPanel from '../components/dashboard/AchievementsPanel';
import CheckinForm from '../components/dashboard/CheckinForm';
import AIDoctorCard from '../components/dashboard/AIDoctorCard';
import DailyTaskCard from '../components/dashboard/DailyTaskCard';
import AssessmentPlanPanel from '../components/dashboard/AssessmentPlanPanel';
import DocumentsPanel from '../components/dashboard/DocumentsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ═══════════ MAIN DASHBOARD ═══════════ */
const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPremium, isPending } = usePremium();
  const [searchParams, setSearchParams] = useSearchParams();

  const [userState, setUserState] = useState(null);
  const [stateLoading, setStateLoading] = useState(true);
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkinData, setCheckinData] = useState({ mood: 5, energy: 5, focus: 5, sleep_hours: 7 });
  const [checkinSaved, setCheckinSaved] = useState(false);
  const [activeChart, setActiveChart] = useState('mood');

  // Post-test celebration
  const [testJustCompleted, setTestJustCompleted] = useState(false);
  const [completedTestName, setCompletedTestName] = useState('');

  // Task state
  const [todayTask, setTodayTask] = useState(null);
  const [taskLoading, setTaskLoading] = useState(true);
  const [taskExpanded, setTaskExpanded] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [userNote, setUserNote] = useState('');
  const [followupAnswer, setFollowupAnswer] = useState('');
  const [skipReason, setSkipReason] = useState('');
  const [completing, setCompleting] = useState(false);
  const [completionDone, setCompletionDone] = useState(false);
  const [completionMsg, setCompletionMsg] = useState('');
  const [showTaskPlayer, setShowTaskPlayer] = useState(false);

  // Plan state
  const [userPlan, setUserPlan] = useState(null);

  const [showShareModal, setShowShareModal] = useState(false);

  // Documents
  const [recentDocs, setRecentDocs] = useState([]);

  /* ── Data fetching ── */
  const loadUserState = useCallback(async () => {
    setStateLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/user/state`, { withCredentials: true });
      setUserState(data);
    } catch (e) { console.error(e); }
    finally { setStateLoading(false); }
  }, []);

  const loadTask = useCallback(async () => {
    setTaskLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/tasks/today`, { withCredentials: true });
      setTodayTask(data);
      if (data.status) { setCompletionDone(true); setSelectedStatus(data.status); }
    } catch (e) { console.error(e); }
    finally { setTaskLoading(false); }
  }, []);

  const loadPlan = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/user/plan`, { withCredentials: true });
      setUserPlan(data);
    } catch (e) { console.error(e); }
  }, []);

  const loadCheckins = async () => {
    try { const { data } = await axios.get(`${BACKEND_URL}/api/checkins?limit=14`, { withCredentials: true }); setRecentCheckins(data); }
    catch (e) { console.error(e); }
  };

  const loadDocs = async () => {
    try { const { data } = await axios.get(`${BACKEND_URL}/api/documents?limit=3`, { withCredentials: true }); setRecentDocs(data); }
    catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (!user?.onboarding_completed) { navigate('/onboarding'); return; }

    const testCompleted = searchParams.get('testCompleted');
    const testName = searchParams.get('testName');
    if (testCompleted === 'true') {
      setTestJustCompleted(true);
      setCompletedTestName(testName || 'Değerlendirme');
      setSearchParams({}, { replace: true });
      setTimeout(() => setTestJustCompleted(false), 8000);
      (async () => {
        try { await axios.post(`${BACKEND_URL}/api/user/state/refresh`, {}, { withCredentials: true }); } catch(e) {}
        loadUserState();
        loadPlan();
      })();
    } else {
      loadUserState();
    }

    loadTask();
    loadPlan();
    loadCheckins();
    loadDocs();
  }, [user, navigate]); // eslint-disable-line

  const submitCheckin = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/checkin`, checkinData, { withCredentials: true });
      setCheckinSaved(true); setShowCheckin(false); loadCheckins();
      setTimeout(() => setCheckinSaved(false), 3000);
      setTimeout(() => loadUserState(), 1000);
    } catch (e) { console.error(e); }
  };

  const submitCompletion = async (status) => {
    if (!todayTask) return;
    setCompleting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/tasks/complete`, {
        task_id: todayTask.task_id, assignment_id: todayTask.assignment_id,
        status, user_note: userNote || null, followup_answer: followupAnswer || null,
        skip_reason: (status === 'skipped' || status === 'postponed') ? skipReason || null : null,
      }, { withCredentials: true });
      setCompletionDone(true); setSelectedStatus(status);
      setCompletionMsg(data.message); setShowCompletion(false);
      setTimeout(() => loadUserState(), 1000);
    } catch (e) { console.error(e); }
    finally { setCompleting(false); }
  };

  const handlePlayerComplete = async (completionData) => {
    if (!todayTask) return;
    setShowTaskPlayer(false);
    setCompleting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/tasks/complete`, {
        task_id: todayTask.task_id,
        assignment_id: todayTask.assignment_id,
        status: completionData.status,
        user_note: completionData.user_note,
        followup_answer: completionData.followup_answer,
        execution_data: completionData.execution_data,
      });
      setCompletionDone(true);
      setSelectedStatus(completionData.status);
      setCompletionMsg(data.message);
      setTimeout(() => loadUserState(), 1000);
    } catch (e) { console.error(e); }
    finally { setCompleting(false); }
  };

  /* ── Derived data ── */
  const chartData = useMemo(() => {
    if (!recentCheckins.length) return [];
    return recentCheckins.slice(0, 14).reverse().map((c, i) => ({
      name: `${i + 1}`,
      'Ruh Hali': c.mood || 5, 'Uyku': c.sleep_hours || 7, 'Odak': c.focus || 5,
    }));
  }, [recentCheckins]);

  const aiMsg = userState?.ai_doctor_message || {};
  const taskStats7d = userState?.task_stats_7d || {};
  const taskStats30d = userState?.task_stats_30d || {};

  /* ── Loading ── */
  if (stateLoading && !userState) {
    return (
      <div className="dashboard-page">
        <BackgroundLayers />
        <Header />
        <SparkleStyles />
        {testJustCompleted && (
          <div className="max-w-7xl mx-auto px-4 pt-8">
            <div className="relative overflow-hidden rounded-[20px] p-5" style={{
              background: 'linear-gradient(160deg, rgba(16,185,129,0.12), rgba(10,18,38,0.85))',
              border: '1px solid rgba(52,211,153,0.35)',
              boxShadow: '0 0 24px rgba(52,211,153,0.2), inset 0 0 20px rgba(52,211,153,0.05)',
              animation: 'fadeSlideIn 0.5s ease-out',
            }} data-testid="test-celebration-banner">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)',
                  boxShadow: '0 0 14px rgba(52,211,153,0.3)',
                }}>
                  <Brain className="w-5 h-5" style={{ color: '#34D399', filter: 'drop-shadow(0 0 6px rgba(52,211,153,0.7))' }} />
                </div>
                <div>
                  <p style={{ color: '#6EE7B7', fontSize: '16px', fontWeight: 700 }}>Tebrikler! {completedTestName} tamamlandı.</p>
                  <p style={{ color: '#AAB4C5', fontSize: '13px', marginTop: '2px' }}>Sonuçlar doktoruna iletildi. Aşağıda yeni değerlendirmeni görebilirsin.</p>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-col items-center justify-center min-h-[70vh]" data-testid="evaluation-loading">
          <div className="relative w-28 h-28 mb-8">
            <div className="absolute inset-0 rounded-full animate-ping" style={{ background: 'rgba(34,211,238,0.15)' }} />
            <div className="relative flex items-center justify-center w-28 h-28 rounded-full" style={{ background: 'linear-gradient(135deg, #22D3EE, #A78BFA)', boxShadow: '0 0 60px rgba(34,211,238,0.4)' }}>
              <Brain className="w-14 h-14 text-white animate-pulse" />
            </div>
          </div>
          <p className="text-white/80 text-lg font-medium">Analiz hazırlanıyor</p>
          <div className="flex items-center gap-2 mt-2">
            <Loader className="w-4 h-4 text-cyan-400 animate-spin" />
            <span className="text-cyan-300 text-sm">Verileriniz birlestiriliyor...</span>
          </div>
        </div>
      </div>
    );
  }

  /* ═══════════ MAIN RENDER — 3 COLUMN LAYOUT ═══════════ */
  return (
    <div className="dashboard-page">
      <PageSEO title="Sağlık Takip Paneli" description="Günlük sağlık takibi, görevler ve AI doktor değerlendirmesi" />
      <BackgroundLayers />
      <Header />

      <GuideModal />
      {checkinSaved && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-2xl flex items-center gap-2 text-white font-medium" style={{
          background: 'linear-gradient(135deg, #34D399, #22D3EE)', boxShadow: '0 0 30px rgba(52,211,153,0.4)',
        }} data-testid="checkin-success-message">
          <CheckCircle className="w-5 h-5" /> Check-in kaydedildi!
        </div>
      )}

      <main className="relative z-10 max-w-7xl mx-auto px-4 pb-16">
        <SparkleStyles />

        {/* Premium Conversion / Pending Banner */}
        {!isPremium && isPending && (
          <div className="mt-6 mb-2 rounded-2xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(250,204,21,0.06), rgba(245,158,11,0.03))',
              border: '1px solid rgba(250,204,21,0.12)',
              boxShadow: '0 0 20px rgba(250,204,21,0.04)',
            }}
            data-testid="dashboard-pending-banner">
            <div className="flex items-center justify-between px-5 py-3.5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(250,204,21,0.1)' }}>
                  <Clock className="w-4 h-4" style={{ color: '#FACC15' }} />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#FACC15' }}>Premium erişiminiz onay bekliyor</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>Genellikle 1–12 saat içinde aktif edilir</p>
                </div>
              </div>
            </div>
          </div>
        )}
        {!isPremium && !isPending && (
          <div className="mt-6 mb-2 rounded-2xl overflow-hidden cursor-pointer transition-all hover:scale-[1.005]" onClick={() => navigate('/premium')}
            style={{
              background: 'linear-gradient(135deg, rgba(250,204,21,0.06), rgba(245,158,11,0.03))',
              border: '1px solid rgba(250,204,21,0.12)',
              boxShadow: '0 0 20px rgba(250,204,21,0.04)',
            }}
            data-testid="dashboard-premium-banner">
            <div className="flex items-center justify-between px-5 py-3.5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(250,204,21,0.1)' }}>
                  <Crown className="w-4 h-4" style={{ color: '#FACC15' }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Premium ile tüm analizlerinizi açın</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>Detaylı AI analizi, trend grafikleri ve daha fazlası</p>
                </div>
              </div>
              <button className="shrink-0 flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-black transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #FACC15, #F59E0B)', boxShadow: '0 4px 12px rgba(250,204,21,0.25)', borderRadius: 4 }}
                data-testid="dashboard-premium-btn">
                <Sparkles className="w-3 h-3" /> Keşfet
              </button>
            </div>
          </div>
        )}

        {/* 3-COLUMN GRID */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* LEFT COLUMN */}
          <div className="lg:col-span-3 space-y-6">
            <PremiumReveal isPremium={isPremium} message="Trend grafiklerinizi görmek için Premium'a geçin">
              <TrendChartPanel
                activeChart={activeChart} setActiveChart={setActiveChart}
                chartData={chartData} aiMsg={aiMsg}
                taskStats7d={taskStats7d} taskStats30d={taskStats30d}
                userState={userState}
              />
            </PremiumReveal>
            <AchievementsPanel userState={userState} taskStats30d={taskStats30d} />
            <CheckinForm
              showCheckin={showCheckin} setShowCheckin={setShowCheckin}
              checkinData={checkinData} setCheckinData={setCheckinData}
              submitCheckin={submitCheckin}
            />
          </div>

          {/* CENTER COLUMN */}
          <div className="lg:col-span-5 space-y-6">
            <PremiumReveal isPremium={isPremium} message="Detaylı AI doktor analizini görmek için Premium'a geçin" revealHeight={400}>
              <AIDoctorCard
                aiMsg={aiMsg} userState={userState} navigate={navigate}
                testJustCompleted={testJustCompleted} completedTestName={completedTestName}
              />
            </PremiumReveal>
            <DailyTaskCard
              taskLoading={taskLoading} todayTask={todayTask} userState={userState}
              completionDone={completionDone} selectedStatus={selectedStatus} setSelectedStatus={setSelectedStatus}
              completionMsg={completionMsg} showCompletion={showCompletion} setShowCompletion={setShowCompletion}
              taskExpanded={taskExpanded} setTaskExpanded={setTaskExpanded}
              userNote={userNote} setUserNote={setUserNote}
              followupAnswer={followupAnswer} setFollowupAnswer={setFollowupAnswer}
              skipReason={skipReason} setSkipReason={setSkipReason}
              completing={completing} submitCompletion={submitCompletion}
              setShowTaskPlayer={setShowTaskPlayer}
            />
          </div>

          {/* RIGHT COLUMN */}
          <div className="lg:col-span-4 space-y-6">
            <AssessmentPlanPanel userPlan={userPlan} onAssessmentClick={(item) => {
              if (item.is_cardio) {
                window.location.href = '/cardio';
              } else if (item.test_id) {
                window.location.href = `/test/${item.test_id}`;
              }
              // If no test_id and not cardio, do nothing (all tests done for this domain)
            }} />
            <DailyVitalsCard cardioScore={userState?.domain_scores?.kalp_dolasim || 0} />
            <PremiumReveal isPremium={isPremium} message="Belge analizlerinizi görmek için Premium'a geçin">
              <DocumentsPanel recentDocs={recentDocs} navigate={navigate} />
            </PremiumReveal>
          </div>

        </div>
      </main>

      <Footer />

      {/* Share Report FAB */}
      <button onClick={() => setShowShareModal(true)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-110"
        style={{
          background: 'linear-gradient(135deg, rgba(59,130,246,0.85), rgba(34,211,238,0.85))',
          boxShadow: '0 0 30px rgba(59,130,246,0.35), 0 4px 20px rgba(0,0,0,0.25)',
          backdropFilter: 'blur(8px)',
        }}
        data-testid="share-report-fab">
        <Share2 className="w-6 h-6 text-white" />
      </button>

      <ShareReportModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} />

      {showTaskPlayer && todayTask && (
        <TaskPlayerModal
          task={todayTask}
          onComplete={handlePlayerComplete}
          onClose={() => setShowTaskPlayer(false)}
        />
      )}
    </div>
  );
};

export default DashboardPage;
